import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import { requestReplayStart } from './statsig-service';

interface StatsigReplayErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface StatsigReplayErrorBoundaryState {
  hasError: boolean;
}

// React error boundaries require class components — no hooks equivalent exists.
export class StatsigReplayErrorBoundary extends Component<
  StatsigReplayErrorBoundaryProps,
  StatsigReplayErrorBoundaryState
> {
  state: StatsigReplayErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): StatsigReplayErrorBoundaryState {
    return { hasError: true };
  }

  componentDidMount(): void {
    window.addEventListener('error', this.handleUnhandledError);
    window.addEventListener(
      'unhandledrejection',
      this.handleUnhandledRejection,
    );
  }

  componentWillUnmount(): void {
    window.removeEventListener('error', this.handleUnhandledError);
    window.removeEventListener(
      'unhandledrejection',
      this.handleUnhandledRejection,
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    requestReplayStart('error_override');
    console.error('[statsig] error boundary captured exception', {
      error,
      componentStack: errorInfo.componentStack,
    });
  }

  private readonly handleUnhandledError = (): void => {
    requestReplayStart('error_override');
  };

  private readonly handleUnhandledRejection = (): void => {
    requestReplayStart('error_override');
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }

    return this.props.children;
  }
}
